import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound, ArrowRight, Eye, EyeOff, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import api from "@/lib/api";

export default function ProRecoverPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("verify"); // "verify" | "reset" | "new-codes"
  const [username, setUsername] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCodes, setNewCodes] = useState([]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!username.trim() || !recoveryCode.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/pro/recover/verify", {
        username: username.trim(),
        recovery_code: recoveryCode.trim(),
      });
      setRecoveryToken(data.recovery_token);
      setStep("reset");
      toast.success("Code verified. Set your new password.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Verification failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/pro/recover/reset", {
        recovery_token: recoveryToken,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      if (data.new_recovery_codes && data.new_recovery_codes.length > 0) {
        setNewCodes(data.new_recovery_codes);
        setStep("new-codes");
        toast.success("Password reset. Save your new recovery codes.");
      } else {
        toast.success("Password reset. Please log in.");
        navigate("/pro/login");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Reset failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto rounded-xl bg-zinc-900 flex items-center justify-center mb-4">
            <KeyRound size={22} className="text-white" />
          </div>
          <h1 className="text-xl font-semibold text-[#111111]">
            {step === "verify" ? "Account Recovery" : "Set New Password"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {step === "verify"
              ? "Enter your username and a recovery code"
              : "Choose a new password for your account"}
          </p>
        </div>

        {step === "verify" ? (
          <form onSubmit={handleVerify} className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-4 shadow-sm">
            <div className="space-y-1.5">
              <Label htmlFor="recover-username" className="text-xs text-zinc-500">Username</Label>
              <Input
                id="recover-username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
                placeholder="your username"
                className="font-mono"
                autoFocus
                data-testid="pro-recover-username"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="recover-code" className="text-xs text-zinc-500">Recovery code</Label>
              <Input
                id="recover-code"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                className="font-mono tracking-widest text-center"
                data-testid="pro-recover-code"
              />
              <p className="text-[10px] text-zinc-500">Enter one of your backup recovery codes</p>
            </div>

            <Button
              type="submit"
              disabled={loading || !username.trim() || !recoveryCode.trim()}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10"
              data-testid="pro-recover-verify-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <>Verify <ArrowRight size={15} className="ml-1.5" /></>}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-4 shadow-sm">
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-xs text-zinc-500">New password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  className="pr-10"
                  autoFocus
                  data-testid="pro-recover-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#888888]"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-xs text-zinc-500">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                data-testid="pro-recover-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10"
              data-testid="pro-recover-reset-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Reset password"}
            </Button>
          </form>
        )}

        {step === "new-codes" && (
          <div className="bg-white rounded-xl border border-[#E5E5E5] p-6 space-y-4 shadow-sm" data-testid="pro-recover-new-codes">
            <p className="text-sm text-[#888888]">
              Your old recovery codes have been replaced. Save these new codes — they are shown <strong className="text-[#111111]">only once</strong>.
            </p>
            <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2">
                {newCodes.map((code, i) => (
                  <div key={i} className="font-mono text-sm text-[#555555] bg-white rounded px-2.5 py-1.5 border border-[#E5E5E5] text-center tracking-wider">
                    {code}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => { navigator.clipboard.writeText(newCodes.join("\n")); toast.success("Copied"); }} data-testid="pro-recover-copy-codes">
                <Copy size={13} className="mr-1.5" /> Copy
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => {
                const text = ["QuickInbox PRO — New Recovery Codes", `Generated: ${new Date().toISOString()}`, "", ...newCodes.map((c, i) => `${i + 1}. ${c}`)].join("\n");
                const blob = new Blob([text], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "quickinbox-recovery-codes.txt";
                document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                toast.success("Downloaded");
              }} data-testid="pro-recover-download-codes">
                <Download size={13} className="mr-1.5" /> Download
              </Button>
            </div>
            <Button onClick={() => navigate("/pro/login")} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10" data-testid="pro-recover-continue-login">
              Continue to login
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-zinc-500 mt-4">
          <button onClick={() => navigate("/pro/login")} className="text-blue-600 hover:text-blue-600">
            Back to login
          </button>
        </p>
      </div>
    </div>
  );
}
